/**
 * whatsNew.js — TEST 2.20 (package WN). D1 (Alex, 2026-09-28): "What's new" is
 * remembered PER DEVICE, not per user on the server, so this touches no API and
 * needs no bundle. The seen version lives in localStorage, on this device only.
 *
 * ★★★ pendingNotes IS A PURE FUNCTION. It takes `running` and `lastSeen` as plain
 * strings and never reads storage itself — src/components/WhatsNewSheet.jsx is the
 * only caller that touches localStorage, through readLastSeenVersion/markSeenVersion
 * below. That split is what makes scripts/test-whats-new.mjs able to run this in
 * plain node, no DOM.
 *
 * ★★★ VERSIONS COMPARE NUMERICALLY, NOT LEXICOGRAPHICALLY. "242.100.0" is NEWER than
 * "242.9.0" — string comparison says the opposite ('1' < '9'). Every comparison here
 * goes through compareSemver, which splits on '.' and compares each segment as a
 * Number.
 *
 * ★★★ A MISSING KEY COUNTS AS SEEN UP TO THE PREVIOUS VERSION. A device that already
 * had the app before this feature existed has no `orella_whats_new_seen` at all — not
 * "seen nothing", which would replay the app's entire release history the first time
 * this ships. It is treated as having seen everything up to (but not including) the
 * release right before `running`, so only the CURRENT release's notes show, once. The
 * "previous version" is read off every entry in `notes` (visible or not — a silent
 * release still marks a point in the release history), not off the visible ones only.
 */

import { TAB_ROUTES } from './tabRoutes.js'

const WHATS_NEW_SEEN_KEY = 'orella_whats_new_seen'
export { WHATS_NEW_SEEN_KEY }

/**
 * mayShowOnPath — TRUE only on the four ordinary tab-bar landing routes
 * (TAB_ROUTES). An ALLOWLIST, not a denylist (MAJOR, review of 184b4c43): see
 * tabRoutes.js for why. Fails closed — any path that is not EXACTLY one of
 * TAB_ROUTES is refused, including every /case/* flow (emergency included), a
 * dynamic /doctor/:id, every /settings/* page, and anything added later that
 * nobody thought to deny.
 */
export function mayShowOnPath(pathname) {
  return TAB_ROUTES.includes(pathname)
}

/**
 * Parse a "N.N.N" version string into [major, minor, patch] numbers. Tolerates an
 * optional leading 'v' (src/utils/appVersion.js's APP_VERSION carries one; the
 * release-notes.json version field and package.json do not). Returns null for
 * anything that isn't exactly three non-negative integer segments.
 */
function parseVersion(v) {
  if (typeof v !== 'string') return null
  const m = v.trim().match(/^v?(\d+)\.(\d+)\.(\d+)$/i)
  if (!m) return null
  return [Number(m[1]), Number(m[2]), Number(m[3])]
}

export function isValidVersion(v) {
  return parseVersion(v) !== null
}

/**
 * Numeric semver compare. Returns <0 if a<b, 0 if equal, >0 if a>b. Callers that
 * pass an unparsable version get NaN back (NaN is never <0, ==0 or >0, so every
 * comparison involving it is false — a malformed version is silently excluded
 * rather than crashing the sheet).
 */
export function compareSemver(a, b) {
  const pa = parseVersion(a)
  const pb = parseVersion(b)
  if (!pa || !pb) return NaN
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i] - pb[i]
  }
  return 0
}

/**
 * Every userVisible entry with lastSeen < version <= running, sorted oldest-first.
 * A silent release (userVisible: false) never appears, at any lastSeen/running.
 */
export function pendingNotes(notes, running, lastSeen) {
  const list = Array.isArray(notes) ? notes : []
  if (!isValidVersion(running)) return []

  let effectiveLastSeen = lastSeen
  if (!isValidVersion(effectiveLastSeen)) {
    const priorVersions = list
      .map((n) => n && n.version)
      .filter((v) => isValidVersion(v) && compareSemver(v, running) < 0)
      .sort(compareSemver)
    // No earlier release at all (this IS the first release) — '0.0.0' sits below
    // every real version, so "> effectiveLastSeen" excludes nothing that matters.
    effectiveLastSeen = priorVersions.length ? priorVersions[priorVersions.length - 1] : '0.0.0'
  }

  return list
    .filter((n) => n && n.userVisible === true && isValidVersion(n.version))
    .filter((n) => compareSemver(effectiveLastSeen, n.version) < 0 && compareSemver(n.version, running) <= 0)
    .sort((a, b) => compareSemver(a.version, b.version))
}

/**
 * s101 (Dr. Rami, 2026-10-12, mockup "Orella What's New Notice" approved): What's new is a
 * NOTIFICATION, not a pop-up. noticeNotes answers what the notice shows and whether it is unread:
 *   - notes the device has not seen yet  -> { entries: pending, unread: true }
 *   - otherwise the latest visible release at or below `running` -> { entries: [it], unread: false }
 *     (it stays in the list as a read item until the next release replaces it)
 *   - nothing visible at all -> { entries: [], unread: false }
 * Pure, like pendingNotes; never throws.
 */
export function noticeNotes(notes, running, lastSeen) {
  const pending = pendingNotes(notes, running, lastSeen)
  if (pending.length) return { entries: pending, unread: true }
  if (!isValidVersion(running)) return { entries: [], unread: false }
  const visible = (Array.isArray(notes) ? notes : [])
    .filter((n) => n && n.userVisible === true && isValidVersion(n.version) && compareSemver(n.version, running) <= 0)
    .sort((a, b) => compareSemver(a.version, b.version))
  return visible.length ? { entries: [visible[visible.length - 1]], unread: false } : { entries: [], unread: false }
}

/**
 * Pick the note items for `language`, falling back to English — the note items
 * follow the app language, with English as the fallback (they are not run through
 * i18n's t(), since they are data, not UI chrome keys).
 */
export function itemsForLanguage(entry, language) {
  const items = entry && entry.items
  if (!items || typeof items !== 'object') return []
  if (Array.isArray(items[language]) && items[language].length) return items[language]
  return Array.isArray(items.en) ? items.en : []
}

/**
 * ★★★ D1: EVERY ACCESS WRAPPED IN TRY/CATCH. A device with storage blocked (private
 * mode, a locked-down browser policy) must never crash the app over a "what's new"
 * sheet — it just sees the sheet again next launch, which is the same experience as
 * a first-ever run.
 */
export function readLastSeenVersion() {
  try {
    return localStorage.getItem(WHATS_NEW_SEEN_KEY)
  } catch (_) {
    return null
  }
}

export function markSeenVersion(version) {
  try {
    localStorage.setItem(WHATS_NEW_SEEN_KEY, version)
  } catch (_) {
    /* best-effort — see the note above readLastSeenVersion */
  }
}
